// Client wrapper around /.netlify/functions/marketdata.
// The function caches FRED/BOI values for 3h and intraday USD/ILS for 15 min
// in Supabase; we add an in-memory 10-min cache so a tab switch doesn't
// re-fire the request while still honoring the 15-min FX freshness.
// Returns: { sp500, nasdaq, dow, treasury10y, fedRate, vix, oilWti, boiRate,
// usdIls }, each either { value, change_pct, date } or null when unavailable.

let _cache   = null;
let _cacheTs = 0;
let _inFlight = null;
const TTL_MS = 10 * 60 * 1000;

export async function fetchMarketData() {
  const now = Date.now();
  if (_cache && now - _cacheTs < TTL_MS) return _cache;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await fetch("/.netlify/functions/marketdata");
      if (!res.ok) {
        console.warn(`[marketdata client] HTTP ${res.status}`);
        return _cache || {};
      }
      const data = await res.json();
      _cache   = data || {};
      _cacheTs = Date.now();
      return _cache;
    } catch (e) {
      console.warn("[marketdata client] fetch failed:", e.message);
      return _cache || {};
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

// Daily closes from Yahoo Finance, through the same function (op=closes).
// → { SYM: { closes: [[date, close], ...], currency } | null }, {} on a
// failure. Kept for 15 minutes per symbol set.
const _closes = new Map();
export async function fetchCloses(symbols, { range = "1mo" } = {}) {
  const list = [...new Set((symbols || []).filter(Boolean))].sort();
  if (!list.length) return {};
  const key = `${range}|${list.join(",")}`;
  const hit = _closes.get(key);
  if (hit && Date.now() - hit.at < 15 * 60 * 1000) return hit.data;
  try {
    const res = await fetch(`/.netlify/functions/marketdata?op=closes&range=${range}&symbols=${encodeURIComponent(list.join(","))}`);
    if (!res.ok) { console.warn(`[marketdata closes] HTTP ${res.status}`); return {}; }
    const data = (await res.json())?.closes || {};
    _closes.set(key, { at: Date.now(), data });
    return data;
  } catch (e) {
    console.warn("[marketdata closes] fetch failed:", e.message);
    return {};
  }
}
